import { ReactNode, useState } from 'react';

import {
  Dot,
  Glow,
  Label,
  Name,
  Time,
} from '@/components/OpsOnDutyBar/OpsOnDutyBar.styles';
import type { OpsSchedule } from '@/services/opsRota/types';
import styled from '@/styles';
import { trackOpsPreviewDayChanged } from '@/util/vexo';

import { dayLabel, shiftDay } from '../dates';
import { useOpsSchedules } from '../hooks/useOpsRota';
import { EMPTY_DAY, LOADING, PREVIEW_LOAD_ERROR } from '../messages';
import { C, IconButton, LinkButton, Muted } from '../Operations.styles';
import { compactClock } from '../scheduleForm';

import { ChevronLeft, ChevronRight, UserGroupIcon } from './icons';

// UI spec §6.
const Card = styled('section', {
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
});

const Header = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '0 16px',
  minHeight: '36px',
});

const Stepper = styled('div', { display: 'flex', alignItems: 'center' });

const StepLabel = styled('span', {
  minWidth: '84px',
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 400,
  whiteSpace: 'nowrap',
});

const Row = styled('ul', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px',
  minHeight: '38px',
  margin: 0,
  padding: '8px 0',
  listStyle: 'none',
});

const Entry = styled('li', {
  flex: 1,
  minWidth: '180px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
});

const Message = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minHeight: '38px',
  padding: '8px 0',
});

export type PreviewRow = {
  key: string;
  scheduleId: number;
  managerId: number;
  firstName: string;
  fullName: string;
  time: string;
};

/**
 * One row per manager per block, sorted by start time then first name.
 * The day before's overnight blocks that run into the day are included,
 * with their own times.
 * Deactivated managers are left out, as the dashboard bar leaves them out
 * (BR-06): this card previews what clients see.
 */
export const previewRows = (schedules: OpsSchedule[]): PreviewRow[] =>
  schedules
    .flatMap((s) =>
      s.managers
        .filter((m) => m.active)
        .map((m) => ({
          key: `${s.id}-${m.id}`,
          scheduleId: s.id,
          managerId: m.id,
          firstName: m.first_name,
          fullName: m.full_name,
          // The instant, so the day before's overnight block leads.
          start: Date.parse(s.starts_at),
          time: `${compactClock(s.start_time)}-${compactClock(s.end_time)}`,
        })),
    )
    .sort((a, b) => a.start - b.start || a.firstName.localeCompare(b.firstName))
    .map(({ start, ...row }) => row);

type Props = {
  /** Ops-zone today (`meta.today`); null until the page has loaded it. */
  today: string | null;
  /**
   * The page could not learn today (its schedules failed), so this card
   * has no day to load either. It says so, with Retry.
   */
  todayError?: boolean;
  onRetryToday?: () => void;
};

// "Ops on duty today" preview with a day stepper. It reads the same blocks
// as the calendar (GET /ops_schedules?from=d&to=d), so it updates after
// every save, repeat or delete for the day it shows. No phone buttons.
const OpsPreviewCard = ({ today, todayError = false, onRetryToday }: Props) => {
  const [date, setDate] = useState<string | null>(null);
  const shown = date ?? today;
  const query = useOpsSchedules(shown ? { from: shown, to: shown } : null, {
    carryOver: true,
  });
  const rows = query.data ? previewRows(query.data.schedules) : [];

  // A failed background refresh keeps the cached rows on show.
  const failed =
    (query.isError && !query.data) || (shown === null && todayError);
  const retry = () => (shown === null ? onRetryToday?.() : query.refetch());

  let content: ReactNode = null;
  if (failed) {
    content = (
      <Message data-testid="opsPreviewError">
        <Muted>{PREVIEW_LOAD_ERROR}</Muted>
        <LinkButton
          type="button"
          data-testid="opsPreviewRetryButton"
          onClick={retry}
        >
          Retry
        </LinkButton>
      </Message>
    );
  } else if (query.data && rows.length === 0) {
    content = (
      <Message>
        <Muted data-testid="opsPreviewEmpty">{EMPTY_DAY}</Muted>
      </Message>
    );
  } else if (query.data) {
    content = (
      <Row aria-label={`Ops scheduled on ${dayLabel(shown as string)}`}>
        {rows.map((row) => (
          <Entry
            key={row.key}
            data-testid={`opsPreviewEntry-${row.managerId}`}
            data-schedule-id={row.scheduleId}
            title={row.fullName}
          >
            <Dot aria-hidden="true" />
            <Name>{row.firstName}</Name>
            <Time>{row.time}</Time>
          </Entry>
        ))}
      </Row>
    );
  } else {
    content = (
      <Message aria-busy="true" data-testid="opsPreviewLoading">
        <Muted>{LOADING}</Muted>
      </Message>
    );
  }

  return (
    <Card data-testid="opsPreviewCard" aria-label="Ops on duty preview">
      <Glow aria-hidden="true" />
      <Header>
        <Label>
          <UserGroupIcon />
          Ops on duty today
        </Label>
        {shown && (
          <Stepper>
            <IconButton
              type="button"
              size="stepper"
              aria-label="Previous day"
              data-testid="opsPreviewPrevButton"
              onClick={() => {
                trackOpsPreviewDayChanged({ direction: 'prev' });
                setDate(shiftDay(shown, -1));
              }}
            >
              <ChevronLeft />
            </IconButton>
            <StepLabel aria-live="polite" data-testid="opsPreviewDateLabel">
              {dayLabel(shown)}
            </StepLabel>
            <IconButton
              type="button"
              size="stepper"
              aria-label="Next day"
              data-testid="opsPreviewNextButton"
              onClick={() => {
                trackOpsPreviewDayChanged({ direction: 'next' });
                setDate(shiftDay(shown, 1));
              }}
            >
              <ChevronRight />
            </IconButton>
          </Stepper>
        )}
      </Header>
      {content}
    </Card>
  );
};

export default OpsPreviewCard;
